import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, MapPin, Euro, FileText, Tag, CalendarDays } from 'lucide-react';
import './HistoricoPage.css';

const HistoricoPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('eventos');
  const [eventos, setEventos] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);

  useEffect(() => {
    fetchHistorico();
  }, []);

  const fetchHistorico = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const eventosResponse = await fetch('https://www.barcodepapel.pt:5017/api/events/my-reservations', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!eventosResponse.ok) {
        throw new Error('Erro ao carregar histórico de eventos');
      }

      const eventosData = await eventosResponse.json();

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const eventosPassados = eventosData.filter(evento => {
        const dataEvento = new Date(evento.eventStartDate);
        dataEvento.setHours(0, 0, 0, 0);
        return dataEvento < hoje;
      });

      setEventos(eventosPassados);

      const agendamentosResponse = await fetch('http://localhost:5017/api/bookings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!agendamentosResponse.ok) {
        throw new Error('Erro ao carregar histórico de agendamentos');
      }

      const agendamentosData = await agendamentosResponse.json();

      const agora = new Date();
      const agendamentosPassados = agendamentosData.filter(agendamento => {
        const dataAgendamento = new Date(agendamento.dataHora);
        return dataAgendamento < agora;
      });

      setAgendamentos(agendamentosPassados);

    } catch (err) {
      console.error('Erro ao buscar histórico:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderEventos = () => {
    if (eventos.length === 0) {
      return (
        <div className="empty-state">
          <Calendar size={64} />
          <p>Ainda não tem eventos no histórico.</p>
        </div>
      );
    }

    return (
      <div className="historico-grid">
        {eventos.map((evento) => (
          <div key={evento.id} className="historico-card">
            <div className="card-header">
              <h3>{evento.eventTitle}</h3>
              <span className="status-badge status-concluido">Concluído</span>
            </div>

            <div className="card-content">
              <div className="info-row">
                <span className="info-label">
                  <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Data do Evento
                </span>
                <span className="info-value">{formatDate(evento.eventStartDate)}</span>
              </div>

              {evento.eventEndDate && (
                <div className="info-row">
                  <span className="info-label">
                    <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Data Final
                  </span>
                  <span className="info-value">{formatDate(evento.eventEndDate)}</span>
                </div>
              )}

              {evento.selectedDays && evento.selectedDays.length > 0 && (
                <div className="info-row">
                  <span className="info-label">
                    <CalendarDays size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Dias Selecionados
                  </span>
                  <div className="selected-days">
                    {evento.selectedDays.map((day, index) => (
                      <span key={index} className="day-badge">{formatDate(day)}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="info-row">
                <span className="info-label">
                  <Users size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Participantes
                </span>
                <span className="info-value">{evento.numberOfParticipants} {evento.numberOfParticipants === 1 ? 'pessoa' : 'pessoas'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">
                  <Euro size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Preço Total
                </span>
                <span className="info-value">{evento.totalPrice.toFixed(2)}€</span>
              </div>

              {evento.children && evento.children.length > 0 && (
                <div className="info-row">
                  <span className="info-label">
                    <Users size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Crianças
                  </span>
                  <div className="children-list">
                    {evento.children.map((child) => (
                      <span key={child.id} className="child-badge">
                        {child.nome}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="info-row">
                <span className="info-label">
                  <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Reservado em
                </span>
                <span className="info-value">{formatDateTime(evento.reservedAt)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAgendamentos = () => {
    if (agendamentos.length === 0) {
      return (
        <div className="empty-state">
          <Calendar size={64} />
          <p>Ainda não tem agendamentos no histórico.</p>
        </div>
      );
    }

    return (
      <div className="historico-grid">
        {agendamentos.map((agendamento) => (
          <div key={agendamento.id} className="historico-card">
            <div className="card-header">
              <h3>{agendamento.servicoNome}</h3>
              <span className="status-badge status-concluido">Concluído</span>
            </div>

            <div className="card-content">
              <div className="info-row">
                <span className="info-label">
                  <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Data e Hora
                </span>
                <span className="info-value">{formatDateTime(agendamento.dataHora)}</span>
              </div>

              {agendamento.duracao && (
                <div className="info-row">
                  <span className="info-label">
                    <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Duração
                  </span>
                  <span className="info-value">{agendamento.duracao}</span>
                </div>
              )}

              {agendamento.preco && (
                <div className="info-row">
                  <span className="info-label">
                    <Euro size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Preço
                  </span>
                  <span className="info-value">{agendamento.preco}</span>
                </div>
              )}

              {agendamento.numeroParticipantes && (
                <div className="info-row">
                  <span className="info-label">
                    <Users size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Participantes
                  </span>
                  <span className="info-value">{agendamento.numeroParticipantes} {agendamento.numeroParticipantes === 1 ? 'criança' : 'crianças'}</span>
                </div>
              )}

              {agendamento.children && agendamento.children.length > 0 && (
                <div className="info-row">
                  <span className="info-label">
                    <Users size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Crianças
                  </span>
                  <div className="children-list">
                    {agendamento.children.map((child) => (
                      <span key={child.id} className="child-badge">
                        {child.nome}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {agendamento.observacoes && (
                <div className="info-row">
                  <span className="info-label">
                    <FileText size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Observações
                  </span>
                  <span className="info-value">{agendamento.observacoes}</span>
                </div>
              )}

              <div className="info-row">
                <span className="info-label">
                  <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Criado em
                </span>
                <span className="info-value">{formatDateTime(agendamento.createdAt)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="historico-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>A carregar histórico...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="historico-page">
      <div className="historico-container">
        <div className="page-header">
          <h1>Histórico</h1>
          <p>Consulte o histórico dos seus eventos e agendamentos</p>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'eventos' ? 'active' : ''}`}
            onClick={() => setActiveTab('eventos')}
          >
            <CalendarDays size={18} />
            Eventos ({eventos.length})
          </button>
          <button
            className={`tab-button ${activeTab === 'agendamentos' ? 'active' : ''}`}
            onClick={() => setActiveTab('agendamentos')}
          >
            <Calendar size={18} />
            Agendamentos ({agendamentos.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'eventos' ? renderEventos() : renderAgendamentos()}
        </div>
      </div>
    </div>
  );
};

export default HistoricoPage;